import orderBy from 'lodash/orderBy';
import {
    INVENTORY_STATUS,
    YARD_CHECK_STATUS_LABEL,
    YARD_CHECK_STATUS,
    YARD_CHECK_ITEM_TYPE,
    YARD_CHECK_ITEM_TYPE_LABEL,
    SUMMARY_ACTION_TEXT,
    SUMMARY_ACTION,
} from 'utils/constants';
import { DATE_TIME_FORMAT, formatDate } from 'utils/dateTimeHelpers';
import { calculateDurationInYard, formatSummaryDuration } from 'utils/inventoryUtils';
import { groupFieldValues } from 'utils/utils';

const { MISSING, NEW, UPDATED, MATCHED } = YARD_CHECK_ITEM_TYPE;
const statusOrder = [MISSING, NEW, UPDATED, MATCHED];

export const mapYardCheckList = (data = []) => {
    const transformData = data.map((item) => {
        const modified = formatDate(item.modified, DATE_TIME_FORMAT);
        const labelStatus = YARD_CHECK_STATUS_LABEL[item.status];
        const finishDate = item.status === YARD_CHECK_STATUS.FINISH ? modified : undefined;
        return {
            ...item,
            startDate: formatDate(item.startDate, DATE_TIME_FORMAT),
            finishDate,
            modified,
            labelStatus,
        };
    });
    return transformData;
};

export const mapYardCheckDetailList = (data = [], inventoryItemTypeList = []) => {
    const inventoryItemTypeKeys = inventoryItemTypeList?.reduce((obj, item) => {
        obj[item.id] = Object.keys(item.fieldTemplate.properties);
        return obj;
    }, {});
    const transformData = data.map((item) => {
        const inventoryItemTypeId = item?.inventoryItemType?.id;
        const selectedInventoryItemTypeKeys = inventoryItemTypeKeys?.[inventoryItemTypeId];
        const srcData = item?.data;
        const cleanObj = selectedInventoryItemTypeKeys?.reduce((obj, key) => {
            const formatKey = key.replace('[]', '');
            obj[formatKey] = Array.isArray(srcData?.[formatKey]) ? srcData?.[formatKey].join(',') : srcData?.[formatKey] || undefined;
            return obj;
        }, {});
        const groupedFieldValues = groupFieldValues(cleanObj);

        const checkInDate = formatDate(item?.checkInDate, DATE_TIME_FORMAT);
        const duration = calculateDurationInYard(item?.checkInDate);
        const durationInYard = formatSummaryDuration(duration);
        const type = item?.inventoryItemType?.name;

        return {
            ...groupedFieldValues,
            ...item,
            checkInDate,
            durationInYard,
            durationInYardMinutes: duration,
            type,
            status: INVENTORY_STATUS[item?.inventoryStatus],
        };
    });
    const sortedData = orderBy(transformData, [(item) => statusOrder.indexOf(item?.status?.toLowerCase())], ['asc']);
    return sortedData;
};

const isExistedInventory = (item, inventoryList) => {
    return inventoryList.find((v) => v.referenceId === item.referenceId && v.inventoryItemType?.id === item.inventoryItemTypeId);
};

const getAction = (item, inventoryList, isCompleted) => {
    const existedInventory = isExistedInventory(item, inventoryList);
    if (!existedInventory || isCompleted) {
        return item.action;
    }
    if (item?.initialInventoryPresence === YARD_CHECK_ITEM_TYPE.MISSING) {
        return SUMMARY_ACTION.REMOVED;
    }
    if (item?.initialInventoryPresence === YARD_CHECK_ITEM_TYPE.NEW) {
        return SUMMARY_ACTION.SKIP;
    }
    return item.action;
};

const getChipStatus = (item, inventoryList, isCompleted) => {
    const existedInventory = isExistedInventory(item, inventoryList);
    if (existedInventory && item?.initialInventoryPresence === YARD_CHECK_ITEM_TYPE.NEW && !isCompleted) {
        return YARD_CHECK_ITEM_TYPE.MATCHED;
    }
    return item?.initialInventoryPresence;
};

export const mapYardCheckSummary = (payload = [], inventoryList = []) => {
    const data = payload.yardCheckItems || [];
    const yardCheck = payload?.yardCheck || {};
    const isYardCheckCompleted = yardCheck?.status === YARD_CHECK_STATUS.FINISH;

    const transformData = data.map((item, index) => {
        const existedInventory = isExistedInventory(item, inventoryList);
        const checkInDate = formatDate(item?.checkInDate || existedInventory?.checkInDate || yardCheck?.startDate, DATE_TIME_FORMAT);
        const duration = calculateDurationInYard(
            item?.checkInDate || existedInventory?.checkInDate || yardCheck?.startDate,
            isYardCheckCompleted ? new Date(yardCheck?.modified) : undefined
        );
        const durationInYard = formatSummaryDuration(duration);
        const type = item?.inventoryItemTypeName;
        const chipStatus = getChipStatus(item, inventoryList, isYardCheckCompleted);
        const groupedFieldValues = groupFieldValues(item.itemData);
        const status = item?.inventoryMappingStatus;
        const action = getAction(item, inventoryList, isYardCheckCompleted);
        const actionText = SUMMARY_ACTION_TEXT[action];

        return {
            autoId: index + 1,
            ...item,
            ...groupedFieldValues,
            checkInDate,
            durationInYard,
            durationInYardMinutes: duration,
            type,
            status: item?.inventoryMappingStatus,
            chipStatus,
            labelChipStatus: YARD_CHECK_ITEM_TYPE_LABEL[chipStatus],
            labelStatus: INVENTORY_STATUS[status],
            yardCheckStatus: YARD_CHECK_STATUS_LABEL[payload?.yardCheck?.status],
            actionText,
            action,
        };
    });

    return orderBy(transformData, [(item) => statusOrder.indexOf(item.chipStatus.toLowerCase())], ['asc']);
};

export const mapYardCheckInfo = (payload = []) => {
    const data = payload?.yardCheck || null;
    const labelStatus = YARD_CHECK_STATUS_LABEL[data?.status];
    return data
        ? {
              ...data,
              labelStatus,
          }
        : null;
};
